import { createContext, useContext, useEffect, useMemo, useReducer, useState, useCallback } from "react";
import PropTypes from "prop-types";
import { useLocation, useNavigate } from "react-router-dom";
import CrudService from "services/cruds-service";
import AuthService from "services/auth-service";
import { clearTokens, saveTokens } from "services/authTokens";
import { toast } from "react-toastify";

// The Material Dashboard 2 PRO React main context
const MaterialUI = createContext();

// The authentication context
export const AuthContext = createContext({
  isAuthenticated: false,
  login: () => {},
  register: () => {},
  logout: () => {},
  getCurrentUser: () => {},
  getCurrentUserData: () => {},
  getRole: () => {},
  getTheme: () => {},
});

const AuthContextProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [accessList, setAccessList] = useState({ readAccess: [], writeAccess: [], readServiceAccess: [], writeServiceAccess: [] })
  const [userDetail, setUserDetail] = useState(null)
  const [loadingUI, setLoadingUI] = useState(false)
  const [entityData, setEntityData] = useState(null)

  const navigate = useNavigate();
  const location = useLocation();

  const token = localStorage.getItem("token");

  // Loading
  const startLoading = useCallback(() => setLoadingUI(true))
  const stopLoading = useCallback(() => setLoadingUI(false))

  const checkAuthentication = useCallback(() => {
    if (!token){
      // navigate("/auth/login");
      return
    };

    setIsAuthenticated(true);
    if(location.pathname === "/auth/login"){
      navigate('/device-dashboard');
    } else if(!location.search){
      navigate(location.pathname);
    } else {
      navigate(location.pathname+location.search);
    }
  }, [token, location.pathname, navigate]);

  useEffect(() => {
    checkAuthentication();
  }, [checkAuthentication]);

  const login = useCallback((newToken, devices, refreshToken) => {
    const returnTo = sessionStorage.getItem("device_claim_return_to");
    sessionStorage.removeItem("device_claim_return_to");
    saveTokens({ access_token: newToken, refresh_token: refreshToken });
    setIsAuthenticated(true);
    setAccessList({ readAccess: [], writeAccess: [], readServiceAccess: [], writeServiceAccess: [] })
    if (returnTo && /^\/onboard\/claim\/RIV-[A-Z0-9-]+$/.test(returnTo)) {
      navigate(returnTo, { replace: true });
      return;
    }
    if (devices?.length === 1) {
      navigate(`/devices/view-device/${devices[0].id}`);
    } else {
      navigate("/device-dashboard");
    }
  }, [navigate]);

  const logout = useCallback(() => {
    clearTokens();
    setIsAuthenticated(false);
    setAccessList({ readAccess: [], writeAccess: [], readServiceAccess: [], writeServiceAccess: [] })
    navigate("/auth/login");
    toast.success("Successfully logged out");
    for (let index = sessionStorage.length - 1; index >= 0; index -= 1) {
      const key = sessionStorage.key(index);
      if (key?.startsWith("rivio_device_installation:") || key === "device_claim_return_to") {
        sessionStorage.removeItem(key);
      }
    }
  }, [navigate]);

  const getCurrentUser = useCallback(async () => {
    try {
      const res = await AuthService.getProfile();
      setUserDetail(res.data.attributes)
      return res.data.id;
    } catch (err) {
      console.error(err);
      return null;
    }
  }, []);

  const getAccessList = useCallback(async () => {
    try {
      const res = await CrudService.getAccessType();
      if(res.success){
        setAccessList(res.data)
      }
      return res.data;
    } catch (err) {
      console.error(err);
      return null;
    }
  }, []);

  const getCurrentUserData = useCallback(async () => {
    try {
      const res = await AuthService.getProfile();
      return res.data;
    } catch (err) {
      console.error(err);
      return null;
    }
  }, []);

  const getRole = useCallback(async () => {
    const id = await getCurrentUser();
    try {
      const res = await CrudService.getUser(id);
      const roleId = 1;
      if (roleId === "1") {
        return "admin";
      }
      if (roleId === "2") {
        return "creator";
      }
      if (roleId === 3) {
        return "member";
      }
      return res.included[0].attributes.name;
    } catch (err) {
      console.error(err);
      return null;
    }
  }, [getCurrentUser]);

  const getTheme = useCallback(async () => {
    const id = await getCurrentUser();
    try {
      const res = await CrudService.getUser(id);
      return res.data.attributes.theme;
    } catch (err) {
      console.error(err);
      return null;
    }
  }, [getCurrentUser]);

  const setEntityInfo = useCallback(async (value) => {
    setEntityData(value)
  }, [])

  return (
    <AuthContext.Provider value={{ isAuthenticated, accessList, userDetail, entityData, setEntityInfo, setIsAuthenticated, login, logout, getRole, getCurrentUser, getTheme, getCurrentUserData, getAccessList, loadingUI, startLoading, stopLoading }}>
      {children}
    </AuthContext.Provider>
  );
};

// Material Dashboard 2 PRO React reducer
function reducer(state, action) {
  switch (action.type) {
    case "MINI_SIDENAV": {
      return { ...state, miniSidenav: action.value };
    }
    case "TRANSPARENT_SIDENAV": {
      return { ...state, transparentSidenav: action.value };
    }
    case "WHITE_SIDENAV": {
      return { ...state, whiteSidenav: action.value };
    }
    case "WHIDTH_SIDENAV": {
      return { ...state, widthnav: action.value };
    }
    case "SIDENAV_COLOR": {
      return { ...state, sidenavColor: action.value };
    }
    case "TRANSPARENT_NAVBAR": {
      return { ...state, transparentNavbar: action.value };
    }
    case "FIXED_NAVBAR": {
      return { ...state, fixedNavbar: action.value };
    }
    case "OPEN_CONFIGURATOR": {
      return { ...state, openConfigurator: action.value };
    }
    case "DIRECTION": {
      return { ...state, direction: action.value };
    }
    case "LAYOUT": {
      return { ...state, layout: action.value };
    }
    case "DARKMODE": {
      return { ...state, darkMode: action.value };
    }
    default: {
      throw new Error(`Unhandled action type: ${action.type}`);
    }
  }
}

// Material Dashboard 2 PRO React context provider
function MaterialUIControllerProvider({ children }) {
  const initialState = {
    miniSidenav: false,
    transparentSidenav: false,
    whiteSidenav: false,
    widthnav: 0,
    sidenavColor: "info",
    transparentNavbar: true,
    fixedNavbar: true,
    openConfigurator: false,
    direction: "ltr",
    layout: "dashboard",
    darkMode: false,
  };

  const [controller, dispatch] = useReducer(reducer, initialState);

  const value = useMemo(() => [controller, dispatch], [controller, dispatch]);

  return <MaterialUI.Provider value={value}>{children}</MaterialUI.Provider>;
}

// Material Dashboard 2 PRO React custom hook for using context
function useMaterialUIController() {
  const context = useContext(MaterialUI);

  if (!context) {
    throw new Error("useMaterialUIController should be used inside the MaterialUIControllerProvider.");
  }

  return context;
}

// Typechecking props for the MaterialUIControllerProvider
MaterialUIControllerProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

// Context module functions
const setMiniSidenav = (dispatch, value) => dispatch({ type: "MINI_SIDENAV", value });
const setTransparentSidenav = (dispatch, value) => dispatch({ type: "TRANSPARENT_SIDENAV", value });
const setWhiteSidenav = (dispatch, value) => dispatch({ type: "WHITE_SIDENAV", value });
const setWidthnav = (dispatch, value) => dispatch({ type: "WHIDTH_SIDENAV", value });
const setSidenavColor = (dispatch, value) => dispatch({ type: "SIDENAV_COLOR", value });
const setTransparentNavbar = (dispatch, value) => dispatch({ type: "TRANSPARENT_NAVBAR", value });
const setFixedNavbar = (dispatch, value) => dispatch({ type: "FIXED_NAVBAR", value });
const setOpenConfigurator = (dispatch, value) => dispatch({ type: "OPEN_CONFIGURATOR", value });
const setDirection = (dispatch, value) => dispatch({ type: "DIRECTION", value });
const setLayout = (dispatch, value) => dispatch({ type: "LAYOUT", value });
const setDarkMode = (dispatch, value) => dispatch({ type: "DARKMODE", value });

export {
  AuthContextProvider,
  MaterialUIControllerProvider,
  useMaterialUIController,
  setMiniSidenav,
  setTransparentSidenav,
  setWhiteSidenav,
  setWidthnav,
  setSidenavColor,
  setTransparentNavbar,
  setFixedNavbar,
  setOpenConfigurator,
  setDirection,
  setLayout,
  setDarkMode,
};
